import { toast } from 'svelte-sonner';

type ApiResultLike = {
    status?: number;
    problem?: {
        detail?: string;
        traceId?: string;
    };
};

export interface ShowApiErrorOptions {
    /** Optional headline shown above the API's `detail` (e.g., "Failed to update segment").
     *  If omitted, the API's `detail` is used as the headline. */
    title?: string;
    /** Fallback message when the API response did not include a `detail`. */
    fallback?: string;
    /** Toast duration in ms. Pass `Number.POSITIVE_INFINITY` to require manual dismissal. */
    duration?: number;
}

/**
 * Render an error toast from an API response, automatically surfacing the `traceId`
 * (with a "Copy" action) when present. Use this whenever you want to show an error
 * toast from a non-OK API result instead of calling `toast.error` directly.
 */
export function showApiError(result: ApiResultLike | undefined | null, options: ShowApiErrorOptions = {}): void {
    const detail = result?.problem?.detail ?? options.fallback ?? 'An unknown error occurred.';
    const traceId = result?.problem?.traceId;

    const headline = options.title ?? detail;
    let description: string | undefined = options.title ? detail : undefined;
    if (traceId) {
        description = description ? `${description}\nReference: ${traceId}` : `Reference: ${traceId}`;
    }

    toast.error(headline, {
        description,
        duration: options.duration ?? 15000,
        ...(traceId && {
            action: {
                label: 'Copy',
                onClick: () => navigator.clipboard.writeText(traceId)
            }
        })
    });
}

/**
 * Global fetchclient `errorCallback`. On protected routes the 401 middleware in hooks.client.ts
 * throws before this can run, so the only 401s that reach it were raised on unauthenticated pages
 * (boot, the login page) — a session that ended, not an error the user can act on.
 */
export function showUnexpectedApiError(result: ApiResultLike | undefined | null): void {
    if (result?.status === 401) return;

    showApiError(result);
}
